import {
  type DataGridProProps,
  GridRowCount,
  type GridRowCountProps,
} from "@mui/x-data-grid-pro";
import { useMemo } from "react";

import { dataGridSettings } from "~/constants/columnDataGridSettings";
import { marketsDataGridSettings } from "~/feature/event/components/MarketsDataGrid/constants";
import {
  type IMarketsDataGridRow,
  isMarketsDataGridMarketRow,
} from "~/feature/event/components/MarketsDataGrid/types";

interface IUseMarketsDataGridFooterCountParams {
  rows: IMarketsDataGridRow[];
  total: number;
  isAllMarketsLoaded: boolean;
  initialRowsLimit: number;
}

interface IMarketsDataGridFooterRowCountProps extends GridRowCountProps {
  visibleMarketRowsCount?: number;
  totalMarketRowsCount?: number;
}

const MarketsDataGridFooterRowCount = ({
  visibleMarketRowsCount,
  totalMarketRowsCount,
  ...props
}: IMarketsDataGridFooterRowCountProps) => {
  return (
    <GridRowCount
      {...props}
      rowCount={totalMarketRowsCount ?? props.rowCount}
      visibleRowCount={visibleMarketRowsCount ?? props.visibleRowCount}
    />
  );
};

export const useMarketsDataGridFooterCount = ({
  rows,
  total,
  isAllMarketsLoaded,
  initialRowsLimit,
}: IUseMarketsDataGridFooterCountParams) => {
  const visibleMarketRowsCount = useMemo(
    () => rows.filter(isMarketsDataGridMarketRow).length,
    [rows]
  );

  const totalMarketRowsCount = useMemo(() => {
    if (isAllMarketsLoaded) {
      return visibleMarketRowsCount;
    }

    return Math.max(total, visibleMarketRowsCount);
  }, [isAllMarketsLoaded, total, visibleMarketRowsCount]);

  const localeText = useMemo(
    () => ({
      ...(dataGridSettings.localeText ?? {}),
      ...(marketsDataGridSettings.localeText ?? {}),
    }),
    []
  );

  const slots = useMemo<DataGridProProps["slots"]>(
    () => ({
      footerRowCount: MarketsDataGridFooterRowCount,
    }),
    []
  );

  const slotProps = useMemo(
    () =>
      (({
        footerRowCount: {
          visibleMarketRowsCount,
          totalMarketRowsCount,
        }
      }) as DataGridProProps["slotProps"]),
    [totalMarketRowsCount, visibleMarketRowsCount]
  );

  return {
    visibleMarketRowsCount,
    totalMarketRowsCount,
    shouldShowToggleButton: totalMarketRowsCount > initialRowsLimit,
    localeText,
    slots,
    slotProps,
  };
};
